import { useEffect, useState, type ReactNode } from 'react';
import { ApiError } from './api';

export function money(amount: number | null | undefined, currency: string | null | undefined) {
  if (amount === null || amount === undefined) return 'n/a';
  try {
    return new Intl.NumberFormat(undefined, currency ? { style: 'currency', currency } : { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
}

export const dateTime = (v: string | null | undefined) => (v ? new Date(v).toLocaleString() : 'n/a');

export function todayIso(offsetDays = 0) {
  const d = new Date(Date.now() + offsetDays * 86_400_000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Loads data for a component and exposes loading / error / reload states. */
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[]) {
  const [state, setState] = useState<{ data: T | null; error: string | null; loading: boolean }>({ data: null, error: null, loading: true });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    fn()
      .then((data) => alive && setState({ data, error: null, loading: false }))
      .catch((e) => alive && setState({ data: null, error: e instanceof ApiError ? e.message : String(e), loading: false }));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { ...state, reload: () => setTick((t) => t + 1) };
}

export function Loadable<T>({ state, children, empty }: { state: { data: T | null; error: string | null; loading: boolean; reload: () => void }; children: (data: T) => ReactNode; empty?: (data: T) => boolean }) {
  if (state.error)
    return (
      <div className="alert error" role="alert">
        {state.error} <button className="link" onClick={state.reload}>Retry</button>
      </div>
    );
  if (state.loading && !state.data) return <div className="muted" aria-busy="true">Loading…</div>;
  if (!state.data) return null;
  if (empty?.(state.data)) return <div className="muted">Nothing here yet.</div>;
  return <>{children(state.data)}</>;
}

export function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

/** Single-series bar chart with per-bar hover tooltip; values are also shown in the table below it. */
export function BarChart({ data, label, format, height = 180 }: { data: { key: string; value: number }[]; label: string; format: (v: number) => string; height?: number }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...data.map((d) => Math.abs(d.value)));
  const min = Math.min(0, ...data.map((d) => d.value));
  const range = max - min || 1;
  const w = 100 / Math.max(data.length, 1);
  const zeroY = (max / range) * (height - 24);
  return (
    <figure className="chart" aria-label={label}>
      <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" role="img" aria-label={label} height={height} width="100%">
        <line x1="0" x2="100" y1={zeroY} y2={zeroY} className="axis" vectorEffect="non-scaling-stroke" />
        {data.map((d, i) => {
          const h = (Math.abs(d.value) / range) * (height - 24);
          const y = d.value >= 0 ? zeroY - h : zeroY;
          return (
            <g key={d.key} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onFocus={() => setHover(i)} onBlur={() => setHover(null)} tabIndex={0}>
              <rect x={i * w} y={0} width={w} height={height} fill="transparent" />
              <rect x={i * w + w * 0.15} y={y} width={w * 0.7} height={Math.max(h, d.value ? 0.5 : 0)} rx={0.6} className={hover === i ? 'bar hover' : 'bar'} />
            </g>
          );
        })}
      </svg>
      <div className="chart-x">
        <span>{data[0]?.key}</span>
        <span>{data[data.length - 1]?.key}</span>
      </div>
      {hover !== null && data[hover] && (
        <div className="tooltip" role="status">
          <strong>{data[hover].key}</strong> {format(data[hover].value)}
        </div>
      )}
    </figure>
  );
}

export function HBars({ rows, format }: { rows: { label: string; value: number; sub?: string }[]; format: (v: number) => string }) {
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.value)));
  return (
    <div className="hbars">
      {rows.map((r) => (
        <div className="hbar" key={r.label} title={`${r.label}: ${format(r.value)}`}>
          <div className="hbar-head">
            <span>{r.label}</span>
            <span className="num">{format(r.value)}{r.sub ? <span className="muted"> · {r.sub}</span> : null}</span>
          </div>
          <div className="hbar-track"><div className="hbar-fill" style={{ width: `${(Math.max(0, r.value) / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && <small className="muted">{hint}</small>}
    </label>
  );
}
